import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { keyframes } from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ProfileCard = styled(motion.div)`
  background: white;
  margin: auto;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 100%; 
  width: 100%;
`;


const GradientText = styled.h1`
  font-weight: 700;
  position: relative;
  padding-bottom: 15px;
  background: linear-gradient(to right, #008a44, #dc3545);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, #008a44, #dc3545);
  }
`;

const SubTitle = styled(GradientText)`
  font-size: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const ProfileDescription = styled(motion.p)`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 16px;
  text-align: justify;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1.5rem;
  padding: 2rem;
  height: 100%;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const ValueCard = styled(motion.div)`
  background: linear-gradient(to right, rgba(0, 138, 68, 0.05), rgba(220, 53, 69, 0.05));
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border-left: 4px solid #008a44;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  background: ${props => props.active ? 'linear-gradient(to right, #008a44, #dc3545)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border: ${props => props.active ? 'none' : '1px solid #ddd'};
  border-radius: 6px;
  margin-right: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${props => props.active ? '600' : '400'};
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(to right, #008a44, #dc3545)' : 'rgba(0,0,0,0.05)'};
  }
`;

const ExpertiseItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const IconCircle = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #008a44;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const DirectorProfileModal = () => {
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: 'Profil' },
    { id: 'services', label: 'Services' },
    { id: 'mission', label: 'Mission & Vision' },
    { id: 'values', label: 'Valeurs' },
    { id: 'clients', label: 'Clients' }
  ];

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'check':
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
      case 'star':
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>;
      case 'target':
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>;
      case 'team':
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>;
      case 'innovation':
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>;
      case 'clients':
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>;
      default:
        return <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
    }
  };

  return (
    <>
      <Header />
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6"
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: "linear-gradient(to right, #f8f9fa, #e6f1e6)"
        }}
      >
        <ProfileCard
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}>

          {/* Header with Image Section */}
          <div className="grid md:grid-cols-3">
            <ImageContainer>
              {/* Image à gauche */}
              <motion.img
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                src="../assets/images/image21.1.png"
                alt="M. Minaflinou V. Wilfrid DEDJI - Expert Comptable"
                className="rounded-full shadow-lg w-48 h-48 object-cover"
              />

              {/* Texte à droite */}
              <div className="flex-col text-right">
                <h2 className="text-2xl font-bold text-gray-800">
                  M. Minaflinou V. Wilfrid <span className="text-red-600 font-extrabold">DEDJI</span>
                </h2>
                <p className="text-gray-600 mt-2 max-w-md">
                  Expert-Comptable diplômé.
                </p>
                <p className="text-danger mt-2 max-w-md">
                  OECCA-BENIN 299-EC.
                </p>
              </div>
            </ImageContainer>


            <div className="p-8 md:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <GradientText className="text-3xl mb-2">
                  CINEF-EC
                </GradientText>

                <ProfileDescription
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  Une jeune équipe de professionnels conduite par M. Minaflinou V. Wilfrid DEDJI.
                </ProfileDescription>

                <ProfileDescription
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  Fort d'une expérience de plus de 20 ans dans des rôles clés de la finance et du management (auditeur, responsable de projet, directeur financier, créateur d'entreprise), M. DEDJI possède une vision globale de l'entreprise qui va bien au-delà de la simple expertise comptable.
                </ProfileDescription>
              </motion.div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="px-8 py-4 border-b border-gray-200 overflow-x-auto whitespace-nowrap">
            <div className="flex">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </TabButton>
              ))}
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {activeTab === 'profile' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Section>
                  <SubTitle className="text-2xl">Un parcours d'excellence au service de votre performance</SubTitle>

                  <ProfileDescription>
                    M. DEDJI est reconnu pour sa capacité à :
                  </ProfileDescription>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span>Anticiper les enjeux organisationnels, financiers et proposer des solutions innovantes et durables.</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('target')}
                    </IconCircle>
                    <span>Transformer les défis complexes en leviers de croissance.</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('star')}
                    </IconCircle>
                    <span>Optimiser la performance dans des domaines variés : finance, ressources humaines, processus opérationnels, gouvernance.</span>
                  </ExpertiseItem>

                  <ProfileDescription className="mt-4">
                    Il apporte une approche stratégique et pragmatique pour accompagner les entreprises dans leur développement et leur transformation.
                  </ProfileDescription>
                </Section>
              </motion.div>
            )}

            {activeTab === 'services' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Section>
                  <SubTitle className="text-2xl">Nos services d'expertise</SubTitle>

                  <ProfileDescription>
                    Nos experts-comptables vous offrent un accompagnement complet :
                  </ProfileDescription>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span><strong>Comptabilité</strong> - Bilan, compte de résultat et tous documents financiers</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span><strong>Fiscalité</strong> - TVA, déclarations fiscales et optimisation</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span><strong>Audit</strong> - Certification des comptes par nos commissaires aux comptes</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span><strong>Juridique</strong> - Création, immatriculation et suivi</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span><strong>Social</strong> - Paie, déclarations et gestion sociale</span>
                  </ExpertiseItem>

                  <ProfileDescription className="mt-4">
                    Concentrez-vous sur votre activité, nous gérons l'essentiel.
                  </ProfileDescription>
                </Section>

                <Section>
                  <SubTitle className="text-2xl">Notre valeur ajoutée</SubTitle>

                  <ProfileDescription>
                    Au-delà de l'expertise comptable, notre valeur ajoutée réside dans notre capacité à vous conseiller. Nous vous apportons un éclairage essentiel pour atteindre vos objectifs et relever vos défis de dirigeant : gestion des risques, optimisation de votre situation personnelle et professionnelle, évaluation de votre entreprise, structuration de votre patrimoine et bien plus encore.
                  </ProfileDescription>

                  <ProfileDescription>
                    En tant que membre d'une profession réglementée et soumis à une déontologie stricte, CINEF-EC vous garantit une confidentialité et une impartialité totale dans le traitement de vos informations, conformément aux exigences de l'Ordre des Experts-Comptables et sous sa supervision.
                  </ProfileDescription>

                  <ValueCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <p className="font-medium">
                      Nous comprenons que chaque projet est différent. C'est pourquoi notre structure tarifaire est conçue pour s'adapter précisément à la complexité des missions qui nous sont confiées. Nous vous invitons à nous contacter afin d'étudier votre projet et de vous fournir un devis personnalisé et transparent.
                    </p>
                  </ValueCard>
                </Section>
              </motion.div>
            )}

            {activeTab === 'mission' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Section>
                  <SubTitle className="text-2xl">Notre mission</SubTitle>

                  <ProfileDescription>
                    Notre mission repose sur 04 axes fondamentalement:
                  </ProfileDescription>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span>Promouvoir la création d'entreprises</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span>Satisfaire les besoins en services d'expertise comptable des entreprises par secteur d'activités</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span>Accompagner les entreprises à la croissance</span>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('check')}
                    </IconCircle>
                    <span>Simplifier la gestion financière de nos clients et leur permettre de se concentrer sur leur cœur de métier, grâce à notre expertise et notre engagement</span>
                  </ExpertiseItem>
                </Section>

                <Section>
                  <SubTitle className="text-2xl">Notre vision</SubTitle>

                  <ValueCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    <p className="font-medium">
                      La Vision partagée par l'ensemble de l'Equipe de CINEF-EC est d'Être un acteur majeur de la réussite de nos clients, être reconnu comme un cabinet qui permet à ses clients de s'épanouir financièrement et d'atteindre leurs objectifs à long terme en leur apportant une valeur ajoutée significative et en contribuant au développement de l'économie nationale.
                    </p>
                  </ValueCard>

                  <ProfileDescription className="mt-4">
                    Être reconnu comme un cabinet qui permet à nos clients de s'épanouir financièrement et d'atteindre leurs objectifs à long terme.
                  </ProfileDescription>
                </Section>
              </motion.div>
            )}

            {activeTab === 'values' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Section>
                  <SubTitle className="text-2xl">Nos valeurs</SubTitle>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('star')}
                    </IconCircle>
                    <div>
                      <h3 className="font-bold text-gray-800">Expertise et Professionnalisme</h3>
                      <p>Nous nous engageons à maintenir un haut niveau d'expertise et de professionnalisme dans toutes nos interventions.</p>
                    </div>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('target')}
                    </IconCircle>
                    <div>
                      <h3 className="font-bold text-gray-800">Confiance et Intégrité</h3>
                      <p>Nous agissons avec transparence, honnêteté et dans le respect de la déontologie de notre profession.</p>
                    </div>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('clients')}
                    </IconCircle>
                    <div>
                      <h3 className="font-bold text-gray-800">Engagement Client</h3>
                      <p>Nous construisons des relations durables avec nos clients, basées sur la proximité, la compréhension de leurs enjeux et un accompagnement sur mesure.</p>
                    </div>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('innovation')}
                    </IconCircle>
                    <div>
                      <h3 className="font-bold text-gray-800">Innovation et Adaptabilité</h3>
                      <p>Nous faisons preuve d'agilité et d'adaptabilité pour accompagner nos clients dans un environnement en perpétuel changement.</p>
                    </div>
                  </ExpertiseItem>

                  <ExpertiseItem
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                  >
                    <IconCircle>
                      {renderIcon('team')}
                    </IconCircle>
                    <div>
                      <h3 className="font-bold text-gray-800">Esprit d'Équipe et Collaboration</h3>
                      <p>Nous favorisons un environnement de travail collaboratif et valorisons l'esprit d'équipe au sein de notre cabinet et, nous travaillons en étroite collaboration avec nos clients pour atteindre leurs objectifs communs.</p>
                    </div>
                  </ExpertiseItem>
                </Section>
              </motion.div>
            )}

            {activeTab === 'clients' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Section>
                  <SubTitle className="text-2xl">Notre clientèle</SubTitle>

                  <ProfileDescription>
                    Des entreprises de toute taille et de divers secteurs d'activité :
                  </ProfileDescription>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <ValueCard
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    >
                      <h3 className="font-bold text-gray-800 mb-2">Types d'entreprises</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Très petites entreprises (TPE)</li>
                        <li>Petites et moyennes entreprises (PME)</li>
                        <li>Entreprises de taille intermédiaire (ETI)</li>
                        <li>Grandes entreprises</li>
                      </ul>
                    </ValueCard>

                    <ValueCard
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      <h3 className="font-bold text-gray-800 mb-2">Secteurs d'activité</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Commerce (détail et gros)</li>
                        <li>Artisanat</li>
                        <li>Bâtiment et Travaux Publics (BTP)</li>
                        <li>Industrie (manufacturière, agroalimentaire, etc.)</li>
                      </ul>
                    </ValueCard>

                    <ValueCard
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      <h3 className="font-bold text-gray-800 mb-2">Services</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Consulting, informatique, communication</li>
                        <li>Transport, logistique</li>
                        <li>Professions libérales (médecins, avocats, architectes, experts)</li>
                        <li>Agriculture</li>
                      </ul>
                    </ValueCard>

                    <ValueCard
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <h3 className="font-bold text-gray-800 mb-2">Autres secteurs</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Hôtellerie et Restauration</li>
                        <li>Secteur de la santé (cliniques, pharmacies)</li>
                        <li>Immobilier (agences, promoteurs)</li>
                        <li>Éducation et formation</li>
                        <li>Art, Culture et divertissement</li>
                        <li>Secteur associatif et organisations à but non lucratif</li>
                        <li>Énergies renouvelables</li>
                        <li>Nouvelles technologies et startups</li>
                      </ul>
                    </ValueCard>
                  </div>

                  <ProfileDescription className="mt-6">
                    La cible de CINEF-EC est donc toute entité économique ayant des obligations comptables, fiscales, sociales et/ou juridiques, et qui a besoin d'un accompagnement professionnel pour gérer ces aspects et optimiser sa performance.
                  </ProfileDescription>
                </Section>
              </motion.div>
            )}
          </div>

          
        </ProfileCard>
      </div>
      <Header />
      <Footer />
    </>
  );
};

export default DirectorProfileModal;